import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { type RemoteDataTrack as TwilioRemoteDataTrack } from "twilio-video";
import { createDerivedWritableAtom } from "../../utils";
import { videoSDKAtom } from "../core/atoms";
import {
  participantCountAtom,
  remoteVideoDelegatorsAtom,
} from "../core/room/participant/atoms";
import { RemoteMediaDelegator } from "../core/room/participant/media/MediaDelegator";
import { QualityMode } from "../core/room/participant/media/types";
import type { RemoteParticipantMediaByParticipantId } from "../core/room/participant/types";

export const twilioIdentityAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "twilioIdentity",
);

export const availableDevicesAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "availableDevices",
);

export const localDataContextAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "localDataContext",
);

export const permissionDeniedAtom = atom((get) => {
  const audioInputDevices = get(availableDevicesAtom)?.audioInputDevices;
  const videoDevices = get(availableDevicesAtom)?.videoInputDevices;

  // The existence of devices with device IDs is the best indicator we have for
  // browser permission status
  // TODO: consider returning `undefined` if we can't determine the status to
  // allow the UI to handle it better
  return {
    microphoneIsBlocked:
      !audioInputDevices ||
      audioInputDevices?.filter((device) => device.deviceId).length === 0,
    videoIsBlocked:
      !videoDevices ||
      videoDevices?.filter((device) => device.deviceId).length === 0,
  };
});

export const videoWasOnAtScreenLeaveAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "videoWasOnAtScreenLeave",
);

export const thumbnailPositionAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "thumbnailPosition",
);

export const isAudioPlaybackRestrictedAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "isAudioPlaybackRestricted",
);

export const usingWebsocketsReverseProxyAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "usingWebsocketsReverseProxy",
);

export const usedTwilioWebsocketsSuccessfullyAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "usedTwilioWebsocketsSuccessfully",
);

export const remoteMediaStatusByParticipantIdAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "remoteMediaStatusByParticipantId",
);

export function removeMediaFromParticipant(
  remoteMediaByParticipantId: RemoteParticipantMediaByParticipantId,
  participantId: string,
  media: RemoteMediaDelegator | TwilioRemoteDataTrack,
) {
  const { kind } = media;
  const existingMediaForParticipant =
    remoteMediaByParticipantId[participantId] ?? {};
  const updatedParticipantMedia = {
    ...existingMediaForParticipant,
    [kind]: existingMediaForParticipant[kind]?.filter(
      (t) => t.sid !== media.sid,
    ),
  };
  return {
    ...remoteMediaByParticipantId,
    [participantId]: updatedParticipantMedia,
  };
}

export function addMediaToParticipant(
  remoteMediaByParticipantId: RemoteParticipantMediaByParticipantId,
  participantId: string,
  media: RemoteMediaDelegator | TwilioRemoteDataTrack,
) {
  const uniqueMediaByParticipantId = removeMediaFromParticipant(
    remoteMediaByParticipantId,
    participantId,
    media,
  );
  const { kind } = media;
  const existingMediaForParticipant =
    uniqueMediaByParticipantId[participantId] ?? {};
  const updatedParticipantMedia = {
    ...existingMediaForParticipant,
    [kind]: [...(existingMediaForParticipant[kind] ?? []), media],
  };
  return {
    ...remoteMediaByParticipantId,
    [participantId]: updatedParticipantMedia,
  };
}

export const videoQualityAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "videoQuality",
  {
    defaultGetValue: QualityMode.High,
  },
);

export const shouldOverrideSuggestedRoomVideoQualityAtom =
  createDerivedWritableAtom(
    videoSDKAtom,
    "shouldOverrideSuggestedRoomVideoQuality",
  );

export const shouldSuggestPerformanceModeAtom = atom((get) => {
  const shouldOverrideSuggestedRoomVideoQuality = get(
    shouldOverrideSuggestedRoomVideoQualityAtom,
  );

  const remoteVideoDelegators = get(remoteVideoDelegatorsAtom);
  const isSomeRemoteVideoLowQuality = remoteVideoDelegators.some(
    (delegator) => {
      return delegator.qualityMode === QualityMode.Low;
    },
  );

  return (
    !shouldOverrideSuggestedRoomVideoQuality && isSomeRemoteVideoLowQuality
  );
});

export const canOverrideSuggestedRoomVideoQualityAtom = atom((get) => {
  const hasParticipants = !!get(participantCountAtom);
  const isHighQuality = get(videoQualityAtom) === QualityMode.High;
  const shouldSuggestPerformanceMode = get(shouldSuggestPerformanceModeAtom);

  return hasParticipants && isHighQuality && shouldSuggestPerformanceMode;
});

export const userDataByParticipantIdAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "userDataByParticipantId",
);

export const userDataFamily = atomFamily((participantId: string) => {
  return atom((get) => {
    const userDataByParticipantId = get(userDataByParticipantIdAtom);
    return userDataByParticipantId[participantId];
  });
});

export const shouldTriggerPictureInPictureAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "shouldTriggerPictureInPicture",
);

export const pictureInPictureParticipantIdAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "pictureInPictureParticipantId",
);

export const dominantSpeakerIdAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "dominantSpeakerId",
);
